/* eslint-disable @typescript-eslint/naming-convention */
import React from "react";
import { ChakraProvider, extendTheme, } from "@chakra-ui/react";
import { Toaster } from "react-hot-toast";
const colors = {
    avocado: "#e8f8ec",
    forest: "#03484d",
    "forest.light": "#4f7f82",
    "forest.dark": "#043d41",
    "forest.darker": "#002528",
    grass: "#4fc26b",
    lightspeed: "#caf23a",
    ocean: "#658df4",
    space: "#222f51",
    brand: {
        "50": "#f9f8ff",
        "100": "#f1f0ff",
        "200": "#e1dcff",
        "300": "#c7bdf9",
        "400": "#9f8cfe",
        "500": "#7159e7",
        "600": "#4928ed",
        "700": "#3a1bd4",
        "800": "#1e0f69",
        "900": "#0e043b",
    },
    gray: {
        "50": "#fcfcfd",
        "100": "#f8fafc",
        "200": "#eef2f6",
        "300": "#e3e8ef",
        "400": "#cdd5df",
        "500": "#99a3b2",
        "600": "#697586",
        "700": "#4b5565",
        "800": "#364152",
        "900": "#121926",
    },
    green: {
        "50": "#fafef5",
        "100": "#f3fee7",
        "200": "#e2fbcb",
        "300": "#d0f7aa",
        "400": "#a6ef67",
        "500": "#85e139",
        "600": "#53a812",
        "700": "#4aa20f",
        "800": "#3b7c0e",
        "900": "#2b5314",
    },
    orange: {
        "50": "#fefcf5",
        "100": "#fffaea",
        "200": "#feefc5",
        "300": "#fddf88",
        "400": "#fec74b",
        "500": "#f79007",
        "600": "#dc6903",
        "700": "#b44707",
        "800": "#93360e",
        "900": "#792e0e",
    },
    red: {
        "50": "#fffafa",
        "100": "#fef3f2",
        "200": "#fee4e2",
        "300": "#fdcdc9",
        "400": "#fca19b",
        "500": "#f97066",
        "600": "#f04438",
        "700": "#d92e21",
        "800": "#b32317",
        "900": "#7a281a",
    },
    blue: {
        "50": "#f5fafe",
        "100": "#eef8ff",
        "200": "#d1e9ff",
        "300": "#b2dcff",
        "400": "#84caff",
        "500": "#54b1fd",
        "600": "#2d90fa",
        "700": "#1370ef",
        "800": "#165cd2",
        "900": "#1a4185",
    },
    plum: {
        "50": "#fcfcfd",
        "100": "#f8f9fc",
        "200": "#eaecf5",
        "300": "#d6d9eb",
        "400": "#b4b7db",
        "500": "#717bbc",
        "600": "#4d5ba6",
        "700": "#3e4684",
        "800": "#271f4d",
        "900": "#0d0526",
    },
    pink: {
        "50": "#fff6fb",
        "100": "#fdf1fa",
        "200": "#fbe7f6",
        "300": "#fcceee",
        "400": "#faa7e0",
        "500": "#f670c7",
        "600": "#ee46bc",
        "700": "#dc2690",
        "800": "#bf1574",
        "900": "#841651",
    },
    yellow: {
        "50": "#fefdf0",
        "100": "#fdfbe8",
        "200": "#fef7c2",
        "300": "#feee95",
        "400": "#fde072",
        "500": "#fac415",
        "600": "#eaaa07",
        "700": "#ca8505",
        "800": "#a15b08",
        "900": "#713b12",
    },
    teal: {
        "50": "#f5fefc",
        "100": "#f0fcf9",
        "200": "#ccfbef",
        "300": "#99f6e0",
        "400": "#5fe8d0",
        "500": "#2fd2b7",
        "600": "#16b79e",
        "700": "#119284",
        "800": "#0e7569",
        "900": "#134e48",
    },
    cyan: {
        "50": "#f4feff",
        "100": "#ecfcfe",
        "200": "#cff8fe",
        "300": "#a6f0fc",
        "400": "#67e3f9",
        "500": "#21ccee",
        "600": "#07aed4",
        "700": "#068ab2",
        "800": "#0d6f90",
        "900": "#164c63",
    },
    fuchsia: {
        "50": "#fefaff",
        "100": "#fdf3ff",
        "200": "#fbe8ff",
        "300": "#f6d0fe",
        "400": "#edaafd",
        "500": "#e577fa",
        "600": "#d444f1",
        "700": "#ba24d5",
        "800": "#9f1ab1",
        "900": "#6f1877",
    },
};
const isPalette = (color) => typeof colors[color] === "object";
const theme = extendTheme({
    components: {
        Button: {
            sizes: {
                sm: {
                    height: "28px",
                },
                md: {
                    height: "36px",
                },
                lg: {
                    height: "48px",
                },
            },
            baseStyle: {
                transitionDuration: "faster",
                _focus: {
                    // Override app styles
                    outline: "none !important",
                },
            },
            variants: {
                solid({ colorScheme }) {
                    const background = isPalette(colorScheme)
                        ? `${colorScheme}.600`
                        : colorScheme;
                    const hoverBackground = isPalette(colorScheme)
                        ? `${colorScheme}.700`
                        : `${colorScheme}.dark`;
                    const disabledBackground = isPalette(colorScheme)
                        ? `${colorScheme}.400`
                        : `${colorScheme}.light`;
                    const activeBackground = isPalette(colorScheme)
                        ? `${colorScheme}.800`
                        : `${colorScheme}.darker`;
                    return {
                        bg: background,
                        _hover: {
                            bg: hoverBackground,
                            _disabled: {
                                bg: disabledBackground,
                            },
                        },
                        _disabled: {
                            opacity: 1,
                            bg: disabledBackground,
                        },
                        _active: {
                            bg: activeBackground,
                        },
                    };
                },
                outline: ({ colorScheme }) => ({
                    color: isPalette(colorScheme) ? `${colorScheme}.700` : colorScheme,
                    borderColor: colorScheme === "gray" ? "gray.400" : "currentColor",
                    bg: "white",
                }),
            },
        },
        Code: {
            variants: {
                subtle: {
                    bg: "gray.200",
                },
            },
        },
        Heading: {
            baseStyle: {
                fontWeight: "semibold",
            },
            sizes: {
                xs: null,
                sm: null,
                md: {
                    fontSize: "xl",
                    lineHeight: "28px",
                },
                lg: {
                    fontSize: "2xl",
                    lineHeight: "28px",
                },
                xl: {
                    fontSize: "3xl",
                    lineHeight: "28px",
                },
                "2xl": {
                    fontSize: "4xl",
                    lineHeight: "32px",
                },
                "3xl": {
                    fontSize: "5xl",
                    lineHeight: "32px",
                },
                "4xl": {
                    fontSize: "6xl",
                    lineHeight: "60px",
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Link: {
            baseStyle: {
                color: "blue.600",
                borderRadius: "sm",
                _hover: {
                    color: "blue.800",
                    textDecoration: "none",
                },
            },
        },
        Avatar: {
            parts: ["container"],
            baseStyle: {
                container: {
                    bg: "gray.200",
                },
                label: {
                    color: "gray.700",
                },
            },
            sizes: {
                md: {
                    container: {
                        width: 7,
                        height: 7,
                        fontSize: "calc(28px / 2.5)",
                    },
                    excessLabel: {
                        width: 7,
                        height: 7,
                    },
                    label: {
                        fontSize: "calc(28px / 2.5)",
                        lineHeight: "28px",
                    },
                },
            },
        },
        Switch: {
            baseStyle: {
                track: {
                    "--switch-track-width": "32px",
                    "--switch-track-height": "18px",
                    "--switch-thumb-x": "14px",
                },
                thumb: {
                    transition: "all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                },
            },
            defaultProps: {
                colorScheme: "brand",
            },
        },
        Checkbox: {
            parts: ["control"],
            sizes: {
                md: {
                    control: {
                        width: 5,
                        height: 5,
                    },
                    icon: {
                        fontSize: "10px",
                    },
                },
            },
            baseStyle: {
                control: {
                    borderRadius: "base",
                    borderWidth: "1px",
                    borderColor: "gray.400",
                    transition: "border-color 100ms cubic-bezier(0.4, 0, 1, 1), background-color 100ms cubic-bezier(0.4, 0, 1, 1), box-shadow 200ms cubic-bezier(0.4, 0, 1, 1)",
                    bg: "white",
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                    _hover: {
                        borderColor: "forest",
                    },
                    _checked: {
                        bg: "avocado",
                        borderColor: "forest",
                        color: "forest",
                        _hover: {
                            bg: "white",
                        },
                        _disabled: {
                            bg: "gray.100",
                            color: "gray.400",
                        },
                    },
                    _indeterminate: {
                        bg: "avocado",
                        borderColor: "forest",
                        color: "forest",
                        _hover: {
                            bg: "white",
                        },
                    },
                    _disabled: {
                        bg: "gray.100",
                        borderColor: "gray.200",
                        color: "gray.400",
                    },
                },
                label: {
                    fontWeight: "medium",
                    color: "gray.700",
                },
            },
            defaultProps: {
                colorScheme: "forest",
            },
        },
        Tooltip: {
            baseStyle: {
                "--tooltip-bg": "#121926",
                px: 3,
                py: 2,
                borderRadius: "md",
                fontSize: "md",
            },
        },
        Badge: {
            baseStyle: {
                fontSize: "sm",
                fontWeight: "semibold",
                letterSpacing: "0.025em",
                px: 2,
                py: 0.5,
                borderRadius: "base",
                borderWidth: "1px",
                borderStyle: "solid",
            },
            variants: {
                subtle: ({ colorScheme }) => ({
                    bg: `${colorScheme}.100`,
                    color: colorScheme === "yellow" ? "yellow.700" : `${colorScheme}.600`,
                    borderColor: `${colorScheme}.400`,
                }),
            },
        },
        Input: {
            baseStyle: {
                field: {
                    transitionDuration: "faster",
                },
            },
            variants: {
                outline: {
                    field: {
                        borderColor: "gray.400",
                        _readOnly: {
                            bg: "gray.100",
                        },
                        _hover: {
                            borderColor: "gray.500",
                            _disabled: {
                                borderColor: "gray.400",
                            },
                            _invalid: {
                                borderColor: "red.600",
                            },
                        },
                        _invalid: {
                            borderColor: "red.600",
                            boxShadow: null,
                        },
                        _focus: {
                            borderColor: "brand.600",
                            _invalid: {
                                borderColor: "red.600",
                            },
                        },
                        _focusVisible: null,
                    },
                },
            },
            sizes: {
                md: {
                    field: {
                        height: "36px",
                        px: 3,
                    },
                },
            },
        },
        Textarea: {
            variants: {
                outline: {
                    borderColor: "gray.400",
                    _readOnly: {
                        bg: "gray.100",
                    },
                    _hover: {
                        borderColor: "gray.500",
                    },
                    _disabled: {
                        _hover: {
                            borderColor: "gray.400",
                        },
                    },
                },
            },
        },
        Form: {
            baseStyle: {
                helperText: {
                    mt: 1,
                    fontSize: "md",
                    color: "gray.600",
                    lineHeight: "tall",
                    maxWidth: "65ch",
                },
            },
        },
        FormLabel: {
            baseStyle: {
                mb: 0,
            },
        },
        FormError: {
            baseStyle: {
                text: {
                    color: "red.600",
                },
            },
        },
        Tabs: {
            baseStyle: {
                tabpanel: {
                    px: 0,
                },
            },
            variants: {
                line: {
                    tab: {
                        borderTopRadius: "sm",
                        position: "relative",
                        _selected: {
                            color: "brand.600",
                            borderColor: "gray.200",
                        },
                        _hover: {},
                    },
                },
            },
        },
        Menu: {
            baseStyle: {
                item: {
                    fontSize: "md",
                    px: 4,
                    py: 2,
                    "> .chakra-menu__icon-wrapper": {
                        display: "flex",
                    },
                    _focus: {
                        // Override app styles
                        outline: "none !important",
                    },
                },
                groupTitle: {
                    fontSize: "sm",
                    textTransform: "uppercase",
                    color: "gray.500",
                    letterSpacing: "0.05em",
                },
            },
        },
        Modal: {
            baseStyle: {
                dialog: {
                    my: 40,
                    p: 6,
                },
                header: {
                    p: 0,
                    fontSize: "xl",
                    fontWeight: "medium",
                },
                body: {
                    mt: 2,
                    p: 0,
                },
                footer: {
                    mt: 8,
                    p: 0,
                    gap: 3,
                },
            },
            defaultProps: {
                size: "lg",
            },
        },
        Progress: {
            baseStyle: {
                track: {
                    bg: "gray.200",
                    borderRadius: "md",
                },
                filledTrack: {
                    transition: "all 100ms cubic-bezier(0.4, 0, 1, 1)",
                },
            },
            sizes: {
                sm: {
                    track: {
                        h: "8px",
                    },
                },
            },
            defaultProps: {
                colorScheme: "brand",
                size: "sm",
            },
        },
        Radio: {
            baseStyle: {
                control: {
                    borderColor: "gray.400",
                    borderWidth: "1px",
                    bg: "white",
                    _hover: {
                        borderColor: "forest",
                    },
                    _focus: {
                        borderColor: "lightspeed",
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                    _checked: {
                        bg: "avocado",
                        borderColor: "forest",
                        color: "forest",
                        _hover: {
                            bg: "white",
                            borderColor: "forest",
                        },
                        _focus: {
                            borderColor: "forest",
                        },
                        _disabled: {
                            bg: "gray.100",
                            color: "gray.400",
                        },
                    },
                    _disabled: {
                        bg: "gray.100",
                        borderColor: "gray.200",
                    },
                },
                label: {
                    fontWeight: "medium",
                    color: "gray.700",
                },
            },
            sizes: {
                md: {
                    control: {
                        w: 5,
                        h: 5,
                    },
                },
            },
            defaultProps: {
                colorScheme: "brand",
            },
        },
        Table: {
            sizes: {
                md: {
                    th: {
                        fontSize: "sm",
                        ":first-child": {
                            pl: 0,
                        },
                    },
                    td: {
                        fontSize: "md",
                        ":first-child": {
                            pl: 0,
                        },
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
    },
    fonts: {
        body: "Inter, system-ui, sans-serif",
        heading: "Inter, system-ui, sans-serif",
    },
    fontSizes: {
        xs: "0px",
        sm: "12px",
        md: "14px",
        lg: "16px",
        xl: "18px",
        "2xl": "21px",
        "3xl": "24px",
        "4xl": "28px",
        "5xl": "32px",
        "6xl": "48px",
        "7xl": "0px",
        "8xl": "0px",
        "9xl": "0px",
    },
    lineHeights: {
        "3": "12px",
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "7": "28px",
        "8": "32px",
        "9": "36px",
        "10": "40px",
    },
    breakpoints: {
        sm: "640px",
        md: "768px",
        lg: "1024px",
        xl: "1280px",
        "2xl": "1536px",
    },
    space: {
        "0.5": "2px",
        "1": "4px",
        "1.5": "6px",
        "2": "8px",
        "2.5": "10px",
        "3": "12px",
        "3.5": "14px",
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "7": "28px",
        "8": "32px",
        "9": "36px",
        "10": "40px",
        "12": "48px",
        "14": "56px",
        "16": "64px",
        "20": "80px",
        "24": "96px",
        "28": "112px",
        "32": "128px",
        "36": "144px",
        "40": "160px",
        "44": "176px",
        "48": "192px",
        "52": "208px",
        "56": "224px",
        "60": "240px",
        "64": "256px",
        "72": "288px",
        "80": "320px",
        "96": "384px",
    },
    sizes: {
        "0.5": "2px",
        "1": "4px",
        "1.5": "6px",
        "2": "8px",
        "2.5": "10px",
        "3": "12px",
        "3.5": "14px",
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "7": "28px",
        "8": "32px",
        "9": "36px",
        "10": "40px",
        "12": "48px",
        "14": "56px",
        "16": "64px",
        "20": "80px",
        "24": "96px",
        "28": "112px",
        "32": "128px",
        "36": "144px",
        "40": "160px",
        "44": "176px",
        "48": "192px",
        "52": "208px",
        "56": "224px",
        "60": "240px",
        "64": "256px",
        "72": "288px",
        "80": "320px",
        "96": "384px",
        "3xs": "224px",
        "2xs": "256px",
        xs: "320px",
        sm: "384px",
        md: "448px",
        lg: "512px",
        xl: "576px",
        "2xl": "672px",
        "3xl": "768px",
        "4xl": "896px",
        "5xl": "1024px",
        "6xl": "1152px",
        "7xl": "1280px",
        "8xl": "1440px",
    },
    radii: {
        sm: "2px",
        base: "4px",
        md: "6px",
        lg: "8px",
        xl: "12px",
        "2xl": "16px",
        "3xl": "24px",
    },
    colors,
    shadows: {
        outline: "0 0 0 4px #F4EBFF",
    },
});
export function ThemeProvider({ children }) {
    return (React.createElement(ChakraProvider, { theme: theme },
        children,
        React.createElement(Toaster, null)));
}
